import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ColDef } from 'ag-grid-community';
import { TableConfig } from '@shared/components/table/table.interfaces';
import { ActionConfig } from '@shared/components/table/table-action-configs';
import {
  ONBOARDING_COLUMNS,
  ONBOARDING_TABLE_CONFIG,
} from '@pages/asset-management/asset-management-table-configuration';
import { AssetManagementService } from '@pages/asset-management/asset-management-service';
import { DateUtils, getMonthDateRange } from '@utils/date.utils';
import { formatSingleDate } from '@utils/utils';
import { yearDateFormat } from '@constants/dateFormat';
import { GlobalErrorService } from '@services/global-error.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MyRequestFormComponent } from '../my-request/my-request-form/my-request-form.component';

export interface OnboardingRequest {
  id: number;
  firstName: string;
  lastName: string;
  emailId: string;
  employeeId: string;
  designation: string;
  department: string;
  location: string;
  doj: string;
  rm: string;
  leader: string;
  company: string;
  laptop: string;
  accessories: string;
  requestedBy: string;
  status: 'Pending' | 'Approved' | 'Allocated' | 'Delivered' | 'Rejected';
  priority: 'High' | 'Medium' | 'Low';
  requestType?: string;
  createdDate: string;
  approvedDate?: string;
}

@Injectable({
  providedIn: 'root',
})
export class OnboardingRequestsTableService {
  private readonly dataSubject = new BehaviorSubject<OnboardingRequest[]>([]);
  private readonly loadingSubject = new BehaviorSubject<boolean>(false);
  private readonly selectedRowsSubject = new BehaviorSubject<
    OnboardingRequest[]
  >([]);

  onboardingData: OnboardingRequest[] = [];

  // Public observables
  public readonly data$ = this.dataSubject.asObservable();
  public readonly loading$ = this.loadingSubject.asObservable();
  public readonly selectedRows$ = this.selectedRowsSubject.asObservable();

  // Configuration
  public readonly columns: ColDef[] = ONBOARDING_COLUMNS;
  public readonly tableConfig: TableConfig = ONBOARDING_TABLE_CONFIG;
  public readonly actions: ActionConfig[] = [
    {
      action: 'view',
      label: 'View Details',
      icon: 'visibility',
      visible: true,
    },
    { action: 'edit', label: 'Edit', icon: 'edit', visible: true },
    { action: 'delete', label: 'Delete', icon: 'delete', visible: true },
  ];

  currentStartDate: string = '';
  currentEndDate: string = '';

  constructor(
    private readonly assetMgmtService: AssetManagementService,
    private errorHandler: GlobalErrorService,
    private dialog: MatDialog,
  ) {
  }

  loadInitialData(): void {
    const { startDate, endDate } = getMonthDateRange(history.state.selectedMonth, yearDateFormat.date);
        this.currentStartDate = startDate;
        this.currentEndDate = endDate;
    this.loadOnboardingData(
      formatSingleDate(this.currentStartDate, yearDateFormat.date),
      formatSingleDate(this.currentEndDate, yearDateFormat.date),
    );
  }

  loadOnboardingData(startDate: string, endDate: string): void {
    this.assetMgmtService.getOnboardingData(startDate, endDate).subscribe({
      next: (response) => {
        this.onboardingData = response?.data;
        this.dataSubject.next(this.onboardingData);
      },
      error: (error) => {
        this.errorHandler.logComponentError(error);
      },
    });
  }

  updateDateRange(startDate: string, endDate: string): void {
    this.currentStartDate = startDate;
    this.currentEndDate = endDate;
  }

  // Action handlers specific to onboarding
  handleAction(action: string, data: OnboardingRequest): void {
    switch (action) {
      case 'view':
        this.viewOnboardingDetails(data);
        break;
      case 'allocate':
        this.allocateAssets(data);
        break;
      case 'approve':
        this.approveOnboarding(data);
        break;
      case 'edit':
        if (data) {
          this.openEditRequestModal(data);
        }
        break;
      case 'create':
        this.openCreateRequestModal();
        break;
      default:
        // Unknown action - handle silently
        break;
    }
  }

  openCreateRequestModal(): void {
    const dialogRef: MatDialogRef<MyRequestFormComponent> = this.dialog.open(
      MyRequestFormComponent,
      {
        width: '1000px',
        maxWidth: '90vw',
        minHeight: '600px',
        maxHeight: '90vh',
        disableClose: false,
        panelClass: 'my-request-modal',
        data: {
          isModal: true,
          isEditMode: false,
        },
      },
    );

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result?.refreshTable) {
        this.loadOnboardingData(this.currentStartDate, this.currentEndDate);
      }
    });
  }

  openEditRequestModal(data: any): void {  
    const dialogRef: MatDialogRef<MyRequestFormComponent> = this.dialog.open(
      MyRequestFormComponent,
      {
        width: '1000px',
        maxWidth: '90vw',
        minHeight: '600px',
        maxHeight: '90vh',
        disableClose: false,
        panelClass: 'my-request-modal',
        data: {
          isModal: true,
          isEditMode: true,
          formData: data,
        },
      },
    );  
    dialogRef.afterClosed().subscribe((result: any) => {
      if (result !== undefined) {
          this.loadOnboardingData(this.currentStartDate, this.currentEndDate);
      }
    });
  }

  private viewOnboardingDetails(data: OnboardingRequest): void {
    // Navigate to details page or open modal
    // this.router.navigate(['/asset-management/onboarding-details', data.id]);
  }

  private allocateAssets(data: OnboardingRequest): void {
    // Update status to 'Allocated'
    const currentData = this.dataSubject.value;
    const updatedData = currentData.map((item) =>
      item.id === data.id ? { ...item, status: 'Allocated' as const } : item,
    );
    this.dataSubject.next(updatedData);
  }

  private approveOnboarding(data: OnboardingRequest): void {
    // Update status to 'Approved'
    const currentData = this.dataSubject.value;
    const updatedData = currentData.map((item) =>
      item.id === data.id ? { ...item, status: 'Approved' as const } : item,
    );
    this.dataSubject.next(updatedData);
  }
}
